import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { applyTheme, themes } from "@/lib/themes";

export interface UserPreferences {
  // UI Preferences
  theme: string;
  chartStyle: "candles" | "line" | "area";
  defaultSlippage: number;
  showConfirmations: boolean;
  soundEffects: boolean;
  animations: boolean;
  compactMode: boolean;

  // Trading Preferences
  defaultBuyAmount: number;
  priorityFeeLevel: "none" | "low" | "medium" | "high" | "turbo";
  autoRefresh: boolean;
  refreshInterval: number;

  // Notification Preferences
  tradeAlerts: boolean;
  priceAlerts: boolean;
  newLaunches: boolean;
  streamerAlerts: boolean;
  communityAlerts: boolean;

  // Privacy
  hideBalances: boolean;
  hidePortfolio: boolean;
  anonymousMode: boolean;
}

const defaultPreferences: UserPreferences = {
  theme: "cyber",
  chartStyle: "candles",
  defaultSlippage: 0.5,
  showConfirmations: true,
  soundEffects: true,
  animations: true,
  compactMode: false,
  defaultBuyAmount: 0.1,
  priorityFeeLevel: "low",
  autoRefresh: true,
  refreshInterval: 5000,
  tradeAlerts: true,
  priceAlerts: true,
  newLaunches: false,
  streamerAlerts: false,
  communityAlerts: true,
  hideBalances: false,
  hidePortfolio: false,
  anonymousMode: false,
};

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
  resetPreferences: () => void;
  isLoaded: boolean;
  availableThemes: typeof themes;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

const STORAGE_KEY = "moonrun_user_preferences";

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPreferences({ ...defaultPreferences, ...parsed });
      }
    } catch (error) {
      console.error("Failed to load preferences:", error);
    }
    setIsLoaded(true);
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
        // Apply theme whenever it changes
        applyTheme(preferences.theme);
      } catch (error) {
        console.error("Failed to save preferences:", error);
      }
    }
  }, [preferences, isLoaded]);

  // Initial theme application
  useEffect(() => {
    if (isLoaded) {
      applyTheme(preferences.theme);
    }
  }, [isLoaded]);

  const updatePreference = <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
    localStorage.removeItem(STORAGE_KEY);
    applyTheme(defaultPreferences.theme);
  };

  return (
    <UserPreferencesContext.Provider
      value={{
        preferences,
        updatePreference,
        resetPreferences,
        isLoaded,
        availableThemes: themes
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error("useUserPreferences must be used within UserPreferencesProvider");
  }
  return context;
}
